import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'

// MUI
import {
  Grid, Paper, Stepper, Step, StepLabel, Button, Typography, TextField, MenuItem, FormControl, Select
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { validateOrgSetup } from '../../../../../redux/actions/integrations'

const CISCO_ERRORS = {
  ORGANIZATION_ERROR: 'ORGANIZATION_ERROR',
  BAD_ORGANIZATION_ERROR: 'BAD_ORGANIZATION_ERROR',
  MANAGEMENT_ERROR: 'MANAGEMENT_ERROR',
  NO_ORGANIZATION: 'NO_ORGANIZATION',
  NO_API_KEY: 'NO_API_KEY',
  NO_API_SECRET: 'NO_API_SECRET'
}

// The text for the stepper
const getSteps = () => {
  return ['Org Setup', 'Unit Destination Mapping']
}

const useSelectStyles = makeStyles((theme) => ({
  select: {
    overflow: 'hidden',
    backgroundColor: '#fff',
    width: '100%',
    padding: theme.spacing(1.5, 2),
    border: '1px solid rgba(0, 0, 0, 0.26)',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    transition: 'background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    '&:focus': {
      backgroundColor: '#f5f5f5',
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px'
    },
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  }
}))

const CiscoNew = (props) => {
  const {
    classes, handleBackTrack, stepperIcons, StepperConnector, ciscoNewForm,
    setCiscoNewForm, orgUnitInfo, handleSubmit
  } = props

  const dispatch = useDispatch()

  const selectClasses = useSelectStyles()

  const steps = getSteps()
  const defaultList = { id: null, name: '--', access: 'block' }

  const [orgSetup, setOrgSetup] = useState('Single Org')
  const [orgID, setOrgID] = useState('')
  const [managementAPIKey, setManagementAPIKey] = useState('')
  const [managementAPISecret, setManagementAPISecret] = useState('')

  const [errors, setErrors] = useState(false)

  const [stepperIndex, setStepperIndex] = useState(0)

  const [listOptions, setListOptions] = useState([defaultList])

  // Makes sure if they are going backwards in the stepper it re-populates the value of the inputs
  useEffect(() => {
    if (ciscoNewForm.summary.setupType !== '') {
      setOrgSetup(ciscoNewForm.summary.setupType)
    }
    if (ciscoNewForm.summary.organizationID !== '') {
      setOrgID(ciscoNewForm.summary.organizationID)
    }
    if (ciscoNewForm.summary.apiKey !== '') {
      setManagementAPIKey(ciscoNewForm.summary.apiKey)
    }
    if (ciscoNewForm.summary.apiSecret !== '') {
      setManagementAPISecret(ciscoNewForm.summary.apiSecret)
    }
  }, [ciscoNewForm])

  // sets the form when block list mappings are updated
  const onChange = (prop, unitID) => e => {
    const event = e
    if (prop === 'blockList') {
      const listOption = listOptions.find((option) => option.name === event.target.value)
      // here we'll need to map through the appropriate object value to set the prop
      setCiscoNewForm(
        {
          ...ciscoNewForm,
          units: {
            ...ciscoNewForm.units,
            [unitID]: {
              unitID,
              destinationListID: listOption.id,
              destinationListName: listOption.name
            }
          }
        })
    } else {
      setCiscoNewForm({ ...ciscoNewForm, [prop]: event.target.value })
    }
  }

  // temporary hard-coded list of options for dropdowns
  const getBlockListOptions = (unitID) => {
    const items = []
    listOptions.forEach((option) => {
      if (option.access && option.access === 'block') {
        items.push(<MenuItem id={`${option.name}-${unitID}`} name={`${option.name}-${unitID}`} key={`${option.id}-${unitID}`} value={option.name}>{option.name}</MenuItem>)
      }
    })
    return items
  }

  // The content of each Stepper's step
  const getStepContent = (stepIndex) => {
    switch (stepIndex) {
      // Name and Description
      case 0:
        return (
          <Grid container item direction='row' className='w-100 mx-auto mb-4'>
            <Grid item container direction='column' spacing={2} className='m-auto w-100'>

              <Grid item container direction='row' justify='center'>
                <Grid container item xs={9} direction='column'>
                  <Typography variant='h6' className='text-primary font-weight-bold'> Organization Setup:</Typography>
                  <Typography gutterBottom variant='caption' style={{ textTransform: 'none' }} className='text-muted'>Cisco Umbrella can be configured under a single Cisco Umbrella account with one or more Destination List policies <span className='font-italic'>Single-Org</span> or can be configured with different Cisco Umbrella accounts for each school <span className='font-italic'>Multi-Org</span>. LearnSafe currently only supports a Single-Org setup.</Typography>
                  <TextField
                    label='Required'
                    error={errors}
                    style={{ textTransform: 'none' }}
                    required
                    variant='outlined'
                    value='Single Org'
                    // helperText={
                    //   errors === CISCO_ERRORS.NO_ORGANIZATION || errors === CISCO_ERRORS.ORGANIZATION_ERROR
                    //     ? 'Error with Organization ID' : errors === CISCO_ERRORS.NO_API_KEY
                    //       ? 'Error with Management Key' : errors === CISCO_ERRORS.NO_API_SECRET
                    //         ? 'Error with Management Secret' : errors === CISCO_ERRORS.MANAGEMENT_ERROR
                    //           ? 'Error with Management Key/Secret' : errors === CISCO_ERRORS.BAD_ORGANIZATION_ERROR
                    //             ? 'The request could not be completed at this time' : ''
                    // }
                    disabled
                  />
                </Grid>
              </Grid>

              <Grid item container direction='row' justify='center'>
                <Grid container item xs={9} direction='column'>
                  <Typography gutterBottom variant='h6' className='text-primary font-weight-bold'> Organization ID:</Typography>
                  <Typography gutterBottom variant='caption' style={{ textTransform: 'none' }} className='text-muted'>Your Cisco Umbrella account has a unique organization ID. This ID can be found by logging into your Cisco Umbrella account. Once you are logged into the correct org, check the URL in the address bar. The URL should be similar to <mark>https://dashboard.umbrella.com/o/OrgID/#/overview.</mark> OrgID represents where your unique Umbrella Org ID is. Enter that value in this field.</Typography>
                  <TextField
                    error={errors === CISCO_ERRORS.NO_ORGANIZATION || errors === CISCO_ERRORS.ORGANIZATION_ERROR}
                    helperText={
                      errors === CISCO_ERRORS.NO_ORGANIZATION
                        ? 'Please enter an Organization ID'
                        : errors === CISCO_ERRORS.ORGANIZATION_ERROR
                          ? 'The requested Organizaiton was not found in Cisco Umbrella'
                          : ''
                    }
                    label='Required'
                    style={{ textTransform: 'none' }}
                    required
                    variant='outlined'
                    value={orgID}
                    onChange={(e) => setOrgID(e.target.value.trim())}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>
              </Grid>

              <Grid item container direction='row' justify='center'>
                <Grid container item xs={9} direction='column'>
                  <Typography gutterBottom variant='h6' className='text-primary font-weight-bold'> Management API Key:</Typography>
                  <Typography gutterBottom variant='caption' style={{ textTransform: 'none' }} className='text-muted'>LearnSafe needs access to your Cisco account using a provisioned Management API to pull destination lists and make changes to destination lists. To generate these, log in to Cisco Umbrella, go to <span className='font-weight-bold'>Admin `{'>'}` API Keys</span>, and click <span className='font-weight-bold'> Create</span>. Select <span className='font-weight-bold'>Umbrella Management</span> and click <span className='font-weight-bold'>Create</span>. Copy your key and secret. Your <span className='font-weight-bold'>Key</span> needs to be entered here.</Typography>
                  <TextField
                    error={errors === CISCO_ERRORS.NO_API_KEY || errors === CISCO_ERRORS.MANAGEMENT_ERROR}
                    helperText={
                      errors === CISCO_ERRORS.NO_API_KEY
                        ? 'Please enter Management Key'
                        : errors === CISCO_ERRORS.MANAGEMENT_ERROR
                          ? 'Management key/secret is incorrect, check Cisco Umbrella Management key/secret or refresh api keys'
                          : ''
                    }
                    label='Required'
                    style={{ textTransform: 'none' }}
                    required
                    variant='outlined'
                    value={managementAPIKey}
                    onChange={(e) => setManagementAPIKey(e.target.value.trim())}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>
              </Grid>

              <Grid item container direction='row' justify='center'>
                <Grid container item xs={9} direction='column'>
                  <Typography gutterBottom variant='h6' className='text-primary font-weight-bold'> Management API Secret:</Typography>
                  <Typography gutterBottom variant='caption' style={{ textTransform: 'none' }} className='text-muted'>LearnSafe needs access to your Cisco account using a provisioned Management API to pull destination lists and make changes to destination lists. To generate these, log in to Cisco Umbrella, go to <span className='font-weight-bold'>Admin `{'>'}` API Keys</span>, and click <span className='font-weight-bold'> Create</span>. Select <span className='font-weight-bold'>Umbrella Management</span> and click <span className='font-weight-bold'>Create</span>. Copy your key and secret. Your Secret needs to be entered here.</Typography>
                  <TextField
                    error={errors === CISCO_ERRORS.NO_API_SECRET || errors === CISCO_ERRORS.MANAGEMENT_ERROR}
                    helperText={
                      errors === CISCO_ERRORS.NO_API_SECRET
                        ? 'Please enter Management Secret'
                        : errors === CISCO_ERRORS.MANAGEMENT_ERROR
                          ? 'Management key/secret is incorrect, check Cisco Umbrella Management key/secret or refresh api keys'
                          : ''
                    }
                    label='Required'
                    style={{ textTransform: 'none' }}
                    required variant='outlined'
                    value={managementAPISecret}
                    onChange={(e) => setManagementAPISecret(e.target.value.trim())}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>
              </Grid>

            </Grid>
          </Grid>
        )
      case 1:
        // Unit/Destination Mapping
        return (
          <Grid container item direction='row' className='w-100 mx-auto mb-4'>
            <Grid item container direction='column' spacing={2} className='m-auto w-100'>
              {/* Map through list of units (represented by the orgUnitInfo variable...probably) */}
              {/* For each unit, display a dropdown (example below) with a hard-coded list of block list values */}
              {/* When a value is set for each dropdown, ensure that the corresponding block list value for the  unit is set */}
              {/* When "Save" is clicked, validate the form */}
              {orgUnitInfo.map(unit => {
                return (
                  <Grid key={`unit-map-${unit.unitID}`} item container direction='row' justify='center'>
                    <Grid item xs={9}>
                      <FormControl fullWidth variant='filled'>
                        <Typography gutterBottom variant='h6' className='text-primary font-weight-bold'>{unit.display}</Typography>
                        {/* <InputLabel htmlFor={`blocklist-label-${unit.unitID}`}>{unit.display}</InputLabel> */}
                        <Select
                          value={
                            ciscoNewForm && ciscoNewForm.units &&
                              ciscoNewForm.units[`${unit.unitID}`] &&
                              listOptions.findIndex((option) => option.name === ciscoNewForm.units[`${unit.unitID}`].destinationListName) > -1
                              ? ciscoNewForm.units[`${unit.unitID}`].destinationListName
                              : '--'
                          }
                          onChange={onChange('blockList', unit.unitID)}
                          classes={selectClasses}
                          // label='test'
                          // labelId={`blocklist-label-${unit.unitID}`}
                          id={`blocklist-input-id-${unit.unitID}`}
                        >
                          {getBlockListOptions(unit.unitID)}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
        )
      case 2:
        // Select Members
        return (
          <></>
        )
      case 3:
        // Review and save
        return (
          <></>
        )
      default:
        return null
    }
  }

  const handleNext = async (step) => {
    // If you are on Organization Information
    if (step === 0) {
      // validate and set data for blocklists
      const response = await new Promise((resolve, reject) => {
        dispatch(
          validateOrgSetup({
            summary: {
              enabled: Boolean(ciscoNewForm.summary.enabled),
              organizationID: orgID,
              setupType: 'S',
              apiKey: managementAPIKey,
              apiSecret: managementAPISecret
            },
            units: {}
          }, resolve, reject)
        )
      })

      if (!response.data || response.error) { setErrors(response.code); return false }

      setErrors(false)
      setListOptions([defaultList, ...response.data])
      setCiscoNewForm({
        ...ciscoNewForm,
        summary: {
          ...ciscoNewForm.summary,
          setupType: orgSetup,
          organizationID: orgID,
          apiKey: managementAPIKey,
          apiSecret: managementAPISecret
        }
      })
      setStepperIndex(stepperIndex + 1)
    }
    // If you are on Select A Unit
    if (step === 1) {
      console.log(ciscoNewForm)
      // TODO check the form for a value for each unit field
      // if (ciscoNewForm.unitID === '') {
      //   setNoUnit(true)
      // } else {
      //   setNoUnit(false)
      //   setStepperIndex(stepperIndex + 1)
      //   dispatch(getOrgUnitInfo(ciscoNewForm.unitID))
      // }
    }
  }

  const handleBack = () => {
    // If you move backward at all in the stepper it will rease you memeber selection
    setStepperIndex(0)
    setCiscoNewForm({
      ...ciscoNewForm,
      supervisors: []
    })
  }

  // Not sure this ever actually fires within the UI or even needs too
  const handleReset = () => {
    setStepperIndex(stepperIndex + 1)
    setCiscoNewForm({
      groupName: '',
      groupDesc: '',
      supervisors: [],
      unitID: ''
    })
  }

  return (
    <Grid container direction='row' className='w-100 mx-auto mt-4 mb-4'>
      <Paper elevation={0} className='w-100 mx-auto'>
        <Grid container item spacing={2} className='w-100 mx-auto' direction='row'>
          <Grid container item alignItems='center' xs={8} style={{ margin: '15px', padding: '5px' }}>
            <Typography
              gutterBottom
              variant='h4'
              style={{ textTransform: 'none', fontWeight: 'bold' }}
              className='text-primary mt-1'
            >
              Configure Umbrella Settings
            </Typography>
          </Grid>
          <Grid container item xs={2} direction='row' justify='flex-end' className='my-auto'>
            <Button size='medium' variant='contained' className={classes.greenBtn} onClick={handleBackTrack}>Cancel</Button>
          </Grid>
        </Grid>
        <Grid container item spacing={2} className='w-100 mx-auto mt-4' direction='column'>
          <Stepper activeStep={stepperIndex} alternativeLabel connector={<StepperConnector />}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconComponent={stepperIcons}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {/* call function for stepper content here */}
          <Grid container item direction='row' className='w-100 mx-auto my-auto'>
            {getStepContent(stepperIndex)}
          </Grid>
          {/* this will be for button options only */}
          <Grid container item spacing={2} className='w-100 mx-auto my-auto' direction='row'>
            {
              stepperIndex === steps.length
                ? (
                  <Grid container item spacing={1}>
                    <Typography className={classes.instructions}>All steps completed</Typography>
                    <Button onClick={handleReset}>Reset</Button>
                  </Grid>)
                : (
                  <Grid container item spacing={1} direction='column' className='w-100 mx-auto' style={{ marginBottom: '20px' }}>
                    <Grid container item justify='space-evenly'>
                      <Button
                        disabled={stepperIndex === 0}
                        onClick={() => handleBack(stepperIndex)}
                        className={classes.backButton}
                      >
                        Back
                      </Button>
                      {stepperIndex === steps.length - 1
                        ? <Button variant='contained' className={classes.saveButton} onClick={handleSubmit}>Save</Button>
                        : <Button variant='contained' className={classes.greenBtn} onClick={() => handleNext(stepperIndex)}>Next</Button>}
                    </Grid>
                  </Grid>)
            }
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  )
}

export default CiscoNew
